import { TABLE_RAYTEAMS_LICENSE } from '../lib/rayteamsTables';
import { DoDB } from '../lib/ddb';
import * as rayteamsLocalClient from '../lib/rayteamsLocalClient';
import { checkPcSpec } from '../lib/pcSpecChecker';
import { SmartDentConstant } from './constants/SmartDentConstant';
import { resolveLicenseProduct, guardPcActivation } from '../lib/licenseProduct';
import { resolveActorFromAuth } from './authActorHelper';
import { isActiveLicenseCode } from '../lib/licenseCodes';
// IMP-151 AC-7: Command(mutation) thunk 결과 생성 헬퍼 + reason enum SSoT.
// Query(fetch) thunk(Check*/Get*)의 data|null 반환은 별도 컨벤션 — 본 모듈 무관.
// 상세: src/lib/pcActivationResult.js
import { PC_FAIL_REASON, thunkOk, thunkFail, thunkFailCode, mapGatewayError } from '../lib/pcActivationResult';

// ─── FEAT-033: PC Seat Activation ────────────────────────────────────────────

// FEAT-056(#560): rayteams 측 localhost API 의 appId path segment 는 제품 목록
// (LicenseProductConstant) 의 product.pcActivation.localAppId 를 SSoT 로 사용한다.
// product.licenseAppId (DDB 할당 레코드 _id) 와 의미상 분리됨. 제품 인자를 생략하면 기본 제품(SMARTDent).
// PC 활성화가 없는 제품은 guardPcActivation 이 거부한다.
// rayteams getFile API 는 path 의 ':'/'/' 를 디코딩하지 않고 그대로 파일 경로로 사용한다.
// 따라서 encodeURIComponent 를 적용하면 '%3A'/'%2F' 가 그대로 디스크 경로 문자열로 들어가
// 404 (Not Found) 가 발생한다. 평문 path 를 그대로 사용해야 동작.
// 검증: Chrome 직접 GET http://localhost:8008/api/file/getFile/C:/Ray/RAYTeams/raylink.json → 200.
const RAYLINK_PATH = 'C:/Ray/RAYTeams/raylink.json';

// IMP-151 AC-2: rayteams local client (localhost:8008) 호출은 lib/rayteamsLocalClient 로 추상화.
// baseURL/timeout/response unwrap interceptor 캡슐화. 이전 인라인 axios.get + unwrapRayteamsResponse
// 패턴 제거.

// localhost:8008/api/file/ping → RAYTeams 설치 여부 확인
const CheckRayTeamsInstalled = () => async (dispatch) => {
    try {
        await rayteamsLocalClient.ping();
        dispatch({ type: SmartDentConstant.SET_RAYTEAMS_INSTALLED, installed: true });
        return true;
    } catch {
        dispatch({ type: SmartDentConstant.SET_RAYTEAMS_INSTALLED, installed: false });
        return false;
    }
};

// localhost:8008/api/file/getFile/... → raylink.json에서 MYLAB_AUTH_USER 추출
// 로그인 안된 경우(빈 문자열) → null 반환.
//
// 실측 (2026-05-08): rayteams getFile 응답의 MYLAB_AUTH_USER 는 escaped JSON 문자열.
//   "{\"custom:region\":...,\"_id\":\"0be...\",\"groupId\":\"4c3...\",...}"
// 객체로 가정하고 직접 ._id 접근 시 undefined 가 되어 로그인 안됨 분기로 잘못 빠진다.
// JSON.parse 후 _id/groupId 추출. 깨진 JSON 인 경우 graceful 하게 null 반환.
//
// PR #214 Round 2 (Sec Major): localhost:8008 은 RAYTeams 가 아닌 임의 로컬 프로세스도 점유 가능.
// 입력의 출처를 신뢰 경계 너머로 가정하고 다음 방어 적용:
//   - 길이 제한 (MAX_RAYLINK_AUTH_USER_LEN) — 거대 입력 DoS 방지.
//   - schema 가드 — _id / groupId 가 정확히 string 인 것만 허용.
//     (객체/배열/__proto__ 키 등은 거부 — prototype pollution 방어).
const MAX_RAYLINK_AUTH_USER_LEN = 10000;
// PR #240 review Sec MAJOR (2026-05-13): 개별 string 필드 (email/name 등) DoS 방어.
// RFC 5321 SMTP email 한도 (256자) 를 기준으로 표시 string 필드 일괄 적용.
const MAX_RAYTEAMS_USER_FIELD_LEN = 256;

/**
 * raylink.json MYLAB_AUTH_USER 를 읽어 Redux store 의 rayteamsUser 에 저장한다.
 *
 * **IMP-222 (2026-05-14)**: rayteamsUser shape contract 확장.
 *   Before: { userId, groupId } — 식별 전용
 *   After:  { userId, groupId, email, name } — 식별 + 표시
 *
 * @returns {Promise<{
 *   userId: string,
 *   groupId: string|null,
 *   email: string|null,
 *   name: string|null
 * }|null>} 실패 시 null. 정상 시 4개 필드 객체.
 *
 * SSoT: raylink.json `MYLAB_AUTH_USER` (RAYTeams 데스크톱 클라이언트가 작성하는 외부
 * 신뢰 경계). 모든 필드는 ① string 타입 강제, ② 빈 문자열 거부, ③ 최대 길이 가드
 * (`MAX_RAYTEAMS_USER_FIELD_LEN` = 256) 통과한 값만 저장. `name` 은
 * name → displayName → userid 우선순위 fallback.
 *
 * 보안 (PR #240 review Sec MAJOR — CWE-1321/20/501/1284):
 *   - JSON.parse 호출 시 reviver 로 `__proto__`/`constructor`/`prototype` 키 차단.
 *   - 개별 string 필드 길이 가드 (`MAX_RAYTEAMS_USER_FIELD_LEN`).
 *   - 전체 raw 길이 가드 (`MAX_RAYLINK_AUTH_USER_LEN`) — 기존 유지.
 */
const GetLocalRayTeamsUser = () => async (dispatch) => {
    try {
        const res = await rayteamsLocalClient.getFile(RAYLINK_PATH);
        const rawAuthUser = res?.MYLAB_AUTH_USER;
        if (!rawAuthUser) {
            dispatch({ type: SmartDentConstant.SET_RAYTEAMS_USER, rayteamsUser: null });
            return null;
        }
        let parsedAuthUser;
        if (typeof rawAuthUser === 'string') {
            if (rawAuthUser.length > MAX_RAYLINK_AUTH_USER_LEN) {
                console.warn('[PcActivationAction.GetLocalRayTeamsUser] MYLAB_AUTH_USER too large');
                dispatch({ type: SmartDentConstant.SET_RAYTEAMS_USER, rayteamsUser: null });
                return null;
            }
            try {
                // PR #240 review Sec MAJOR (CWE-1321 Prototype Pollution): reviver 로
                // __proto__ / constructor / prototype 키 차단. raylink.json 은 외부
                // 신뢰 경계이므로 객체 prototype chain 오염 방어.
                parsedAuthUser = JSON.parse(rawAuthUser, (key, value) => (
                    key === '__proto__' || key === 'constructor' || key === 'prototype'
                        ? undefined
                        : value
                ));
            } catch (parseErr) {
                console.warn('[PcActivationAction.GetLocalRayTeamsUser] MYLAB_AUTH_USER parse error');
                dispatch({ type: SmartDentConstant.SET_RAYTEAMS_USER, rayteamsUser: null });
                return null;
            }
        } else {
            // 방어: 미래 호환성 — rayteams 가 객체로 직접 보낼 수도 있는 경우 그대로 사용.
            parsedAuthUser = rawAuthUser;
        }
        // schema 가드: _id 와 groupId 는 string 만 허용. 객체/배열은 거부.
        const userId = typeof parsedAuthUser?._id === 'string' ? parsedAuthUser._id : null;
        const groupId = typeof parsedAuthUser?.groupId === 'string' ? parsedAuthUser.groupId : null;
        if (!userId) {
            dispatch({ type: SmartDentConstant.SET_RAYTEAMS_USER, rayteamsUser: null });
            return null;
        }
        // BUG-113 (사용자 요청 2026-05-13): matchedVoucher 없을 때도 PCActivationPanel 에
        // RAYTeams 계정 정보를 표시하기 위해 raylink.json MYLAB_AUTH_USER 의 email/name
        // 등 식별 정보를 함께 추출.
        // PR #240 review Sec MAJOR (CWE-1284 Improper Validation of Specified Quantity):
        //   개별 string 필드 길이 가드 추가 — 거대 입력 DoS / 표시 영역 오염 방어.
        //   기본값 256자 (RFC 5321 SMTP email 한도).
        const pickStr = (v, maxLen = MAX_RAYTEAMS_USER_FIELD_LEN) => (
            typeof v === 'string' && v.length > 0 && v.length <= maxLen ? v : null
        );
        const email = pickStr(parsedAuthUser?.email);
        const name = pickStr(parsedAuthUser?.name)
            || pickStr(parsedAuthUser?.displayName)
            || pickStr(parsedAuthUser?.userid);
        const rayteamsUser = { userId, groupId, email, name };
        dispatch({ type: SmartDentConstant.SET_RAYTEAMS_USER, rayteamsUser });
        return rayteamsUser;
    } catch {
        dispatch({ type: SmartDentConstant.SET_RAYTEAMS_USER, rayteamsUser: null });
        return null;
    }
};

// IMP-175: PC 시스템 정보 조회 + 권장사양 비교. RAYTeams getinfo endpoint.
//   응답을 pcSpecChecker 로 변환하여 systemSpecCheck state 에 저장 ({ ok, failures, measured }).
//   실패 시 systemSpecCheck = null (UI 가 silent 처리 — 권장사양 미상 시 alert 미표시).
//
// 보안 (PR #223 review #6):
//   - localhost 응답은 임의 로컬 프로세스가 변조 가능. spec check 결과를 활성화/권한 게이트로 사용 금지 (안내 전용).
//   - getSystemInfo() 원본 payload 에 rtid 등 식별자 포함 가능 → pcSpecChecker 에 전달 전 명시적 화이트리스트
//     ({ data: { host, cpuInfo, gpuInfo } }) 로 추출하여 silent PII drift 방어 (defense-in-depth).
const GetSystemSpecInfo = () => async (dispatch) => {
    try {
        const payload = await rayteamsLocalClient.getSystemInfo();
        // 응답 화이트리스트 — pcSpecChecker 가 사용하는 필드만 추출. rtid 등 향후 추가될 PII 차단.
        const inner = payload?.data ?? payload ?? {};
        const sanitizedPayload = {
            host: inner.host ?? {},
            cpuInfo: Array.isArray(inner.cpuInfo) ? inner.cpuInfo : [],
            gpuInfo: Array.isArray(inner.gpuInfo) ? inner.gpuInfo : [],
        };
        const result = checkPcSpec(sanitizedPayload);
        dispatch({ type: SmartDentConstant.SET_SYSTEM_SPEC_CHECK, systemSpecCheck: result });
        return result;
    } catch (err) {
        // PR #223 review #7: sanitized warn 로깅 (200자 cap, PR #214 Round 2 패턴).
        console.warn('[IMP-175] GetSystemSpecInfo failed:', String(err?.message || err).slice(0, 200));
        dispatch({ type: SmartDentConstant.SET_SYSTEM_SPEC_CHECK, systemSpecCheck: null });
        return null;
    }
};

// localhost:8008/api/license/check/{appId}?purpose=check → 현재 PC 라이선스 상태 조회
const CheckLocalLicense = (product) => async (dispatch, getState) => {
    // FEAT-056: PC 활성화 없는 제품 거부(warn 은 가드가 남김). 조회 thunk 의 실패값(null) 반환, dispatch 없음.
    const guarded = guardPcActivation(product, { handler: 'PcActivationAction.CheckLocalLicense', getState });
    if (!guarded) return null;
    try {
        const payload = await rayteamsLocalClient.getLicenseStatus(guarded.pcActivation.localAppId);
        dispatch({
            type: SmartDentConstant.SET_LOCAL_LICENSE_STATUS,
            localLicenseStatus: payload,
        });
        return payload;
    } catch {
        dispatch({ type: SmartDentConstant.SET_LOCAL_LICENSE_STATUS, localLicenseStatus: null });
        return null;
    }
};

// localhost:8008/api/license/check/{appId}?purpose=use → 현재 PC 활성화
//
// CQS (Command-Query Separation) 패턴: Activate/Deactivate 는 명령만 수행하고 Redux 상태를
// 직접 dispatch 하지 않는다. localLicenseStatus 갱신은 호출자(PCActivationPanel)가 후속으로
// CheckLocalLicense() 를 dispatch 하여 fresh 한 상태를 받아 SET_LOCAL_LICENSE_STATUS 로
// 일원화한다 (Activate 후 즉시 상태 동기화 필요 시).
const ActivateCurrentPC = (product) => async (dispatch, getState) => {
    const guarded = guardPcActivation(product, { handler: 'PcActivationAction.ActivateCurrentPC', getState });
    if (!guarded) return thunkFail(PC_FAIL_REASON.INVALID_PARAMS);
    try {
        const payload = await rayteamsLocalClient.activateLicense(guarded.pcActivation.localAppId);
        // BUG-164: check===true 만으로는 불충분 — A0001(미관리 app) 등도 true 반환. 활성 코드(L0001~L0003)만 성공.
        if (payload?.check === true && isActiveLicenseCode(payload?.code)) {
            return thunkOk({ status: payload });
        }
        // BUG-164: check=true 인데 활성 코드가 아니어서 실패로 강등하는 케이스 관측 (rayteams 신규/변경 코드 조기 감지).
        if (payload?.check === true) {
            console.warn('[BUG-164] ActivateCurrentPC: check=true 이나 비활성 코드 — fail-closed', {
                handler: 'PcActivationAction.ActivateCurrentPC',
                code: payload?.code,
                reason: payload?.reason,
            });
        }
        return thunkFailCode(payload?.code);
    } catch {
        return thunkFail(PC_FAIL_REASON.NETWORK_ERROR);
    }
};

// localhost:8008/api/license/check/{appId}?purpose=revoke → 현재 PC 비활성화
// (ActivateCurrentPC 와 동일하게 CQS 패턴 — 상태 dispatch 없음, 호출자가 CheckLocalLicense 재호출)
const DeactivateCurrentPC = (product) => async (dispatch, getState) => {
    const guarded = guardPcActivation(product, { handler: 'PcActivationAction.DeactivateCurrentPC', getState });
    if (!guarded) return thunkFail(PC_FAIL_REASON.INVALID_PARAMS);
    try {
        const payload = await rayteamsLocalClient.revokeLicense(guarded.pcActivation.localAppId);
        return thunkOk({ status: payload });
    } catch {
        return thunkFail(PC_FAIL_REASON.NETWORK_ERROR);
    }
};

// BUG-111 (2026-05-12): GetPCActivations — rayteams gateway (DoDB) query 로 이관.
// Confluence "Local License Check API doc" v22 §"RAYGuard 에서 License Used(Seat
// activated list) 확인 방법". 기존 BE GET /voucher-license/{id}/pcs 라우트는 BUG-108
// 에서 제거됐고, 본 PR 로 신규 데이터 모델 (단일 record 의 used[] 배열) 채택.
//
// 인자 변경: voucherLicenseId (MongoDB _id) → voucherSk (lcuse: prefix sk).
// 호출자 (PCActivationPanel) 도 license.sk 를 전달하도록 함께 변경.
//
// 반환: used[] 에서 revoke !== true 인 PC array.
//
// BUG-112 (RTSQA-340, 2026-05-13): options.dispatchRedux=false 옵션 추가 —
// LicenseDetailModal 의 local state 격리 의도를 보장하기 위해 모든 Redux mutation
// (LOADING/ERROR/GET_PC_ACTIVATIONS) 을 skip. 기본값 true 라 기존 caller
// (PCActivationPanel) 동작은 영향 없음. LicenseDetailModal 만 false 로 호출.
//
// FIXME(IMP-210, Arch): boolean flag 분기 (control coupling antipattern).
// 본 thunk 가 (a) 순수 DDB fetch 와 (b) Redux mutation 두 책임을 dispatchRedux 로
// 분기. IMP-210 (SmartDentAction service layer 분리) 시 다음 구조로 자연 통합:
//   - lib/pcActivationService.js: fetchPCActivationsRaw(voucherSk) — 순수 fetch
//   - SmartDentAction.GetPCActivations: thunk wrapper (service 호출 + Redux dispatch)
//   - LicenseDetailModal: service 직접 호출 (thunk 불필요)
// 그 시점에 dispatchRedux 옵션 제거 가능.
/**
 * 주어진 voucherSk 의 PC activation 목록 조회.
 *
 * @param {string} voucherSk - lcuse: prefix DDB sk (PCActivationPanel/LicenseDetailModal 공통).
 * @param {Object} [options] - 호출 옵션.
 * @param {Object} [options.product] - 라이선스 제품(FEAT-056). 생략 시 기본 제품(SMARTDent).
 *   PC 활성화가 없는 제품이면 dispatch 없이 null 반환(가드가 warn).
 * @param {boolean} [options.dispatchRedux=true] - Redux state mutation 여부.
 *   false 면 SET_PC_ACTIVATION_LOADING / SET_PC_ACTIVATION_ERROR / GET_PC_ACTIVATIONS
 *   세 가지 type dispatch 를 모두 skip. LicenseDetailModal 의 local state 격리용 (BUG-112).
 * @param {boolean} [options.withLogs=false] - activation history 동행 반환.
 *   false (기본): 기존 동작 — revoke 제외 PC array 반환 (호출자 전체 회귀 차단).
 *   true: `{ active: <기존 array>, logs: <activationLogs 배열> }` 반환.
 *   RTSQA-339 (2026-05-14): LicenseDetailModal 의 Activation History 섹션 lazy fetch
 *   에서 사용. activationLogs 는 DeactivateOtherPC 가 list_append 하는 voucherSk 레코드
 *   최상위 배열. 항목 schema: { rtid, created, type, actor, pcinfo }.
 * @returns {Promise<Array|{active: Array, logs: Array}|null>}
 *   기본: array. withLogs=true: { active, logs }. 실패: null.
 */
const GetPCActivations = (voucherSk, options = {}) => async (dispatch, getState) => {
    const { dispatchRedux = true, withLogs = false } = options ?? {};
    // FEAT-056: 로딩 dispatch 보다 먼저 거부 — 로딩 상태가 켜진 채 남지 않게. 기존 실패값(null) 반환.
    const guarded = guardPcActivation(options?.product, { handler: 'PcActivationAction.GetPCActivations', getState });
    if (!guarded) return null;
    if (dispatchRedux) {
        dispatch({ type: SmartDentConstant.SET_PC_ACTIVATION_LOADING, loading: true });
    }
    if (!isValidVoucherSk(voucherSk)) {
        if (dispatchRedux) {
            dispatch({
                type: SmartDentConstant.SET_PC_ACTIVATION_ERROR,
                error: { status: null, code: 'INVALID_PARAMS', reason: 'INVALID_VOUCHER_SK' },
            });
        }
        return null;
    }
    try {
        // BUG-111 (2026-05-12): PC seat used[] 레코드의 _id 는 제품의 pcActivation.localAppId.
        // product.licenseAppId (allocation/voucher record _id) 와 의미상 분리. Confluence v22
        // §"RAYGuard 에서 License Used(Seat activated list) 확인 방법" 명시.
        // localhost API path 와 동일 식별자 (FEAT-056: 제품 목록에서 파생).
        const list = await DoDB('query', {
            TableName: TABLE_RAYTEAMS_LICENSE,
            KeyConditionExpression: '#_id = :_id and #sk = :sk',
            ExpressionAttributeNames: { '#_id': '_id', '#sk': 'sk' },
            ExpressionAttributeValues: {
                ':_id': guarded.pcActivation.localAppId,
                ':sk': voucherSk,
            },
        });
        const record = Array.isArray(list) ? list[0] : null;
        const used = Array.isArray(record?.used) ? record.used : [];
        const pcs = used.filter((pc) => pc && pc.revoke !== true);
        // BUG-111 / IMP-192 (2026-05-12): activationcount — 누적 활성화 횟수
        // (Confluence v22 record.activationcount). 같은 PC 가 activate→revoke→re-activate
        // 시 +1 카운트. limit 5 검사 (MAX_UNIQUE_PCS) 에 사용.
        //
        // PR #234 review (Minor #1): Number.isFinite 사용 — typeof NaN === 'number' 라
        //   NaN 통과 시 uniqueLimitCount >= 5 비교가 항상 false → limit 무력화 회귀 차단.
        // PR #234 review (Minor #2): fallback `used.length` — rayteams 가 같은 PC 재활성화
        //   시 신규 element append 라면 정확. in-place 업데이트라면 등록된 PC 수와 동등 (누적
        //   카운트 ≠ used.length). rayteams 동작이 확실 명시되어 있지 않으므로 fallback 은
        //   "BE 응답에 activationcount 누락" 경우의 best-effort — activationcount 도착 시 즉시
        //   대체됨. 정확성은 record.activationcount 신뢰에 의존.
        const activationCount = Number.isFinite(record?.activationcount)
            ? record.activationcount
            : used.length;
        if (dispatchRedux) {
            dispatch({ type: SmartDentConstant.GET_PC_ACTIVATIONS, pcActivations: pcs, activationCount });
        }
        // RTSQA-339: withLogs 옵션 시 activationLogs 동행 반환.
        // 배열 가드 — record.activationLogs 가 누락/비배열일 때 logs=[] 안전 반환.
        if (withLogs) {
            const logs = Array.isArray(record?.activationLogs) ? record.activationLogs : [];
            return { active: pcs, logs };
        }
        return pcs;
    } catch (error) {
        // PR #214 review (Security Minor): error.response.data verbatim 저장 → 화이트리스트.
        // status / code / message 만 추출. 그 외 BE 응답 필드는 Redux 에 누출되지 않도록 차단.
        // PR #214 Round 2 (Sec Major): reason 길이 제한 — gateway unhandled exception 의 stack/
        //   SQL/내부 식별자가 message 필드로 누출 가능. 200자 제한.
        const MAX_REASON_LEN = 200;
        const status = error?.response?.status;
        const errCode = typeof error?.response?.data?.code === 'string'
            ? error.response.data.code.slice(0, 50)
            : null;
        const rawReason = typeof error?.response?.data?.message === 'string'
            ? error.response.data.message
            : (typeof error?.message === 'string' ? error.message : 'UNKNOWN');
        const reason = rawReason.slice(0, MAX_REASON_LEN);
        if (dispatchRedux) {
            dispatch({
                type: SmartDentConstant.SET_PC_ACTIVATION_ERROR,
                error: { status, code: errCode, reason },
            });
        }
        return null;
    }
};

// ─── BUG-109: voucher_license DDB sync — rayteams gateway 경유 ───────────────
//
// BUG-108 (BE PR #113) 로 BE 의 rayteams-license DDB 호출이 전면 제거되었다.
// rayguard-api Lambda 의 IAM 이 rayteams-license 테이블 (rayteams 팀 소유) 권한
// 없음. FE 가 rayteams gateway (api-teams-info.rayteams.com/common/calldynamo,
// DoDB wrapper) 로 직접 갱신.
//
// FE 가 보장 가능한 보안 가드 3건 (BE 가 했던 5건 중):
//   - `attribute_exists(sk)` ConditionExpression — phantom write 차단
//   - `suspendedBy = actor.adminid` audit — Redux AuthReducer 신뢰 추출 (※ wire-format
//     에서 신원 검증 부재 — gateway 자체 인증/인가에 의존. 클라이언트 신뢰는 제한적)
//   - sk 형식 정규식 — `lcuse:` prefix + GUID (DeactivateOtherPC) — sk 키 인젝션 차단
// 나머지 2건 (dealer-scope IDOR, voucherSk 서버측 lookup) 은 dealer 화면에서
// 이미 가드된 license 객체를 그대로 사용 + rayteams gateway 인증 위임.
//
// PR #227 Round 1 review 대응:
//   - SuspendVoucherLicenseDdb/ResumeVoucherLicenseDdb 의 query → update 2-step 제거.
//     primary key (_id, sk) equality 매칭은 항상 0~1건 → 단일 update + attribute_exists
//     으로 round-trip 절감 + race window 단축 (review Major #3/#7/Perf).
//   - audit `:by` 두 함수에도 기록 (Blocker #1 / Major #2).
//   - voucherSk 형식 검증 isValidVoucherSk 추가 (Major #4).
//   - 공통 update 헬퍼 추출 (Minor #10).

// BUG-111: rtid 형식 검증 — used[] 배열의 PC 식별자 (raylink.json RAYLINK_ID).
// `#`/`:` 등 sk 구분자 포함 시 ConditionExpression 인젝션 위험 → RFC 4122 GUID 정규식.
// (BUG-111 review Minor #4: legacy isValidGuid 제거 — used[] 마이그레이션 후 호출처 0건.)
const isValidRtid = (r) => typeof r === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(r);

// voucher_license sk 형식 검증 — src/lib/rayteamsTables.js 의 lcuse 규약 기준:
//   `lcuse:{region}:{groupId}:{userId}[:suffix]`. prefix 가 'lcuse:' 가 아니거나
//   PC seat 합성 sk (`...#guid`) 가 들어오면 의도와 다른 레코드 갱신 위험 → 차단.
const isValidVoucherSk = (s) =>
    typeof s === 'string' &&
    s.length > 0 &&
    s.length <= 256 &&
    s.startsWith('lcuse:') &&
    !s.includes('#');

// 공용 헬퍼 — voucher 레코드 단일 update. attribute_exists(sk) 로 phantom write 차단.
// SuspendVoucherLicenseDdb / ResumeVoucherLicenseDdb 가 본 헬퍼를 공유 (drift 방지).
//
// 반환 매핑:
//   ConditionalCheckFailed → { ok: true, updatedCount: 0 } — DDB 레코드 아직 미생성
//     (BE 가 신규 voucher 발급 후 DDB 가 lazy 생성하는 케이스 — idempotent 처리).
//   기타 에러 → { ok: false, reason: 'GATEWAY_ERROR' }
const updateVoucherLicenseDdb = async (voucherSk, updateSpec, product) => {
    try {
        await DoDB('update', {
            TableName: TABLE_RAYTEAMS_LICENSE,
            Key: { _id: product.licenseAppId, sk: voucherSk },
            ConditionExpression: 'attribute_exists(sk)',
            ...updateSpec,
        });
        return thunkOk({ updatedCount: 1 });
    } catch (error) {
        if (/ConditionalCheckFailed/i.test(String(error?.message || ''))) {
            console.warn('[guard] voucher license DDB sync matched 0 records', {
                handler: 'PcActivationAction.updateVoucherLicenseDdb',
                target: { sk: voucherSk, licenseAppId: product?.licenseAppId },
                reason: 'ConditionalCheckFailed — DDB record not created yet (lazy creation)',
            });
            return thunkOk({ updatedCount: 0 });
        }
        throw error;
    }
};

// BUG-111 (2026-05-12) — 타 PC 원격 비활성화. 데이터 모델 변경:
// 기존: 합성 sk `${voucherSk}#${guid}` 의 별도 레코드 update.
// 신규: 단일 레코드 (key: {_id, sk: voucherSk}) 의 used[] 배열에서 rtid 매칭 element 의
//       revoke=true / revokedAt / revokedBy mark.
//
// 인자: (voucherSk, rtid) — guid → rtid 명칭 변경 (Confluence v22 used[].rtid).
//
// 흐름:
//   1. DoDB('query') 로 used 배열 + 현재 인덱스 확보 (race window 최소화 위해 즉시 update)
//   2. used 배열에서 rtid 매칭 element 의 인덱스 N 찾기
//   3. 이미 revoke=true 면 idempotent { ok:true, alreadyRevoked:true } 반환 (update 생략)
//   4. UPDATE SET used[N].revoke=true, used[N].revokedAt, used[N].revokedBy
//      ConditionExpression: used[N].rtid = :rtid  — race 시 ConditionalCheckFailed
//
// 에러 contract — 의도적으로 GetPCActivations 와 다른 패턴 (BUG-111 review Major #5):
//   · GetPCActivations: Redux dispatch (SET_PC_ACTIVATION_ERROR) + null 반환
//     — pcActivations slice 의 단일 상태 동기화가 본질
//   · DeactivateOtherPC: { ok:false, reason } 객체 반환만 (Redux dispatch 없음)
//     — 호출자가 user-facing toast/메시지로 직접 처리. STALE_USED_LIST 같은 retry
//       가능한 reason 을 호출자가 즉시 판단해야 함. Redux 중간 단계 불요.
//   호출자는 패턴 차이를 인지하고 각각 적절한 UX (slice vs toast) 적용.
const DeactivateOtherPC = (voucherSk, rtid, product) => async (dispatch, getState) => {
    // FEAT-056: PC 활성화 없는 제품 거부(warn 은 가드가 남김). 명령 thunk 이므로 INVALID_PARAMS.
    const guarded = guardPcActivation(product, { handler: 'PcActivationAction.DeactivateOtherPC', getState });
    if (!guarded) return thunkFail(PC_FAIL_REASON.INVALID_PARAMS);
    if (!isValidVoucherSk(voucherSk)) {
        return thunkFail(PC_FAIL_REASON.INVALID_PARAMS);
    }
    if (!isValidRtid(rtid)) {
        return thunkFail(PC_FAIL_REASON.INVALID_PARAMS);
    }
    const actor = resolveActorFromAuth(getState);
    if (!actor) return thunkFail(PC_FAIL_REASON.UNAUTHENTICATED);
    const now = Date.now();
    try {
        // (1) query — 현재 used 배열 + 인덱스 확보.
        // BUG-111: PC seat record 의 _id 는 제품의 pcActivation.localAppId.
        // product.licenseAppId 와 의미상 분리 — GetPCActivations 와 동일.
        const list = await DoDB('query', {
            TableName: TABLE_RAYTEAMS_LICENSE,
            KeyConditionExpression: '#_id = :_id and #sk = :sk',
            ExpressionAttributeNames: { '#_id': '_id', '#sk': 'sk' },
            ExpressionAttributeValues: {
                ':_id': guarded.pcActivation.localAppId,
                ':sk': voucherSk,
            },
        });
        const record = Array.isArray(list) ? list[0] : null;
        if (!record) return thunkFail(PC_FAIL_REASON.PHANTOM_PC);
        const used = Array.isArray(record.used) ? record.used : [];
        const targetIndex = used.findIndex((pc) => pc && pc.rtid === rtid);
        if (targetIndex < 0) return thunkFail(PC_FAIL_REASON.PHANTOM_PC);
        // (3) idempotent — 이미 revoke 됐으면 update 생략.
        if (used[targetIndex].revoke === true) {
            return thunkOk({ alreadyRevoked: true });
        }
        // (4) list element update — rtid 검증 ConditionExpression 으로 race 방어.
        // BUG-111 사용자 보고 (2026-05-12):
        //  - DDB ValidationException "revoke is reserved keyword" → #revoke 등 escape.
        //  - activationLogs 에 revoke 이력 함께 append — 활성/비활성 history 일관성.
        //    type='deactivation' + pcinfo 는 used[targetIndex].pcinfo 복사 (동일 PC 식별).
        //    list_append + if_not_exists 로 activationLogs 부재 케이스도 안전.
        const targetUsed = used[targetIndex];
        const newLog = {
            rtid,
            created: now,
            pcinfo: targetUsed?.pcinfo || {},
            type: 'deactivation',
            actor: actor.id,
        };
        await DoDB('update', {
            TableName: TABLE_RAYTEAMS_LICENSE,
            Key: { _id: guarded.pcActivation.localAppId, sk: voucherSk },
            ConditionExpression: `used[${targetIndex}].rtid = :rtid`,
            UpdateExpression: `SET used[${targetIndex}].#revoke = :t, used[${targetIndex}].#revokedAt = :now, used[${targetIndex}].#revokedBy = :by, #activationLogs = list_append(if_not_exists(#activationLogs, :emptyList), :newLog)`,
            ExpressionAttributeNames: {
                '#revoke': 'revoke',
                '#revokedAt': 'revokedAt',
                '#revokedBy': 'revokedBy',
                '#activationLogs': 'activationLogs',
            },
            ExpressionAttributeValues: {
                ':t': true,
                ':now': now,
                ':by': actor.id,
                ':rtid': rtid,
                ':newLog': [newLog],
                ':emptyList': [],
            },
        });
        return thunkOk();
    } catch (error) {
        console.error('[PcActivationAction.DeactivateOtherPC]', error?.message ?? error);
        if (/ConditionalCheckFailed/i.test(String(error?.message || ''))) {
            // race — query 후 update 사이에 다른 호출이 used 배열 수정. FE 호출자 재시도 가능.
            return thunkFail(PC_FAIL_REASON.STALE_USED_LIST);
        }
        return thunkFail(mapGatewayError(error));
    }
};

// IMP-170 (AC-1): DeactivatePc — DeactivateOtherPC 의 의미 명시 alias.
// 'Other' 는 FEAT-033 '현재 PC 외' 컨텍스트 잔재라 LicenseDetailModal 의 라이선스별 PC
// 관리에서 의미 누수. 정식 호출명: LicenseDetailModal / PCActivationPanel 모두 DeactivatePc 사용.
// DeactivateOtherPC 는 deprecated — export 는 하위호환을 위해 유지.
const DeactivatePc = DeactivateOtherPC;

// BUG-109 — SuspendLicense (BE MongoDB) 성공 후 호출. voucherSk 매칭 DDB 레코드의
// suspended=true / suspendedAt=now / suspendedBy=actor.id 설정. BE 원본 ddbSuspendRecord
// 와 동등 동작 + audit.
//
// PR #227 Round 1: query → update 2-step 제거. (_id, sk) equality 는 단일 레코드
// 매칭이므로 단일 update with attribute_exists 가 동등. round-trip 절감 + race window
// 단축. ConditionalCheckFailed = 레코드 없음 → idempotent ok:true updatedCount:0.
const SuspendVoucherLicenseDdb = (voucherSk, product) => async (dispatch, getState) => {
    // FEAT-056: 정지는 PC 활성화 없는 제품에서도 동작해야 하므로 가드 아님 — 제품만 해석(목록 밖이면 거부).
    const resolved = resolveLicenseProduct(product, { handler: 'PcActivationAction.SuspendVoucherLicenseDdb', getState });
    if (!resolved) return thunkFail(PC_FAIL_REASON.INVALID_PARAMS);
    if (!isValidVoucherSk(voucherSk)) {
        return thunkFail(PC_FAIL_REASON.INVALID_PARAMS);
    }
    const actor = resolveActorFromAuth(getState);
    if (!actor) return thunkFail(PC_FAIL_REASON.UNAUTHENTICATED);
    try {
        return await updateVoucherLicenseDdb(voucherSk, {
            UpdateExpression: 'SET suspended = :t, suspendedAt = :now, suspendedBy = :by',
            ExpressionAttributeValues: { ':t': true, ':now': Date.now(), ':by': actor.id },
        }, resolved);
    } catch (error) {
        console.error('[PcActivationAction.SuspendVoucherLicenseDdb]', error?.message ?? error);
        return thunkFail(mapGatewayError(error));
    }
};

// BUG-109 — ResumeLicense (BE MongoDB) 성공 후 호출. voucherSk 매칭 DDB 레코드의
// suspended=false 복귀 + suspendedAt 필드 REMOVE + resumedBy=actor.id audit.
// PR #227 Round 1: 동일 단순화 (단일 update with attribute_exists).
const ResumeVoucherLicenseDdb = (voucherSk, product) => async (dispatch, getState) => {
    // FEAT-056: Suspend 와 동일 — 가드 아님, 제품만 해석.
    const resolved = resolveLicenseProduct(product, { handler: 'PcActivationAction.ResumeVoucherLicenseDdb', getState });
    if (!resolved) return thunkFail(PC_FAIL_REASON.INVALID_PARAMS);
    if (!isValidVoucherSk(voucherSk)) {
        return thunkFail(PC_FAIL_REASON.INVALID_PARAMS);
    }
    const actor = resolveActorFromAuth(getState);
    if (!actor) return thunkFail(PC_FAIL_REASON.UNAUTHENTICATED);
    try {
        return await updateVoucherLicenseDdb(voucherSk, {
            UpdateExpression: 'SET suspended = :f, resumedAt = :now, resumedBy = :by REMOVE suspendedAt, suspendedBy',
            ExpressionAttributeValues: { ':f': false, ':now': Date.now(), ':by': actor.id },
        }, resolved);
    } catch (error) {
        console.error('[PcActivationAction.ResumeVoucherLicenseDdb]', error?.message ?? error);
        return thunkFail(mapGatewayError(error));
    }
};

export const PcActivationAction = {
    CheckRayTeamsInstalled,
    GetLocalRayTeamsUser,
    CheckLocalLicense,
    GetSystemSpecInfo,
    ActivateCurrentPC,
    DeactivateCurrentPC,
    GetPCActivations,
    DeactivateOtherPC, // deprecated: use DeactivatePc (IMP-170)
    DeactivatePc,
    SuspendVoucherLicenseDdb,
    ResumeVoucherLicenseDdb,
};
